import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Tabs,
  Tab,
  Chip,
  Box,
  CircularProgress,
  Typography,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { EmojiEvents } from '@material-ui/icons';
import { usePageStyles } from 'components/Page/PageKit';
import { MOBILE_QUERY, useIsMobile } from 'components/Page/useIsMobile';
import { Waypoint } from 'react-waypoint';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  DuelFeedData, DuelFeedQuery, DuelFeedVars,
  CharacterDuelRecordData, CharacterDuelRecordQuery, UserDuelRecordData, UserDuelRecordQuery, DuelRecordVars,
} from '@pvplevelup/core/dist/queries/duel/duel';
import { IDuel, IDuelPerformance } from '@pvplevelup/core/dist/interfaces/IDuel';
import { AocClasses, IWinLoss } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';
import { formatDuration } from './DuelRoundBreakdown';

const WIN_COLOR = '#43a047';
const LOSS_COLOR = '#e53935';
const TIE_COLOR = '#757575';

const useStyles = makeStyles((theme) => ({
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: `${theme.spacing(2)}px`,
    marginBottom: theme.spacing(2),
    [MOBILE_QUERY]: {
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: `${theme.spacing(1)}px`,
      marginBottom: theme.spacing(1.5),
    },
  },
  // phone layout: one card per duel
  mDuel: {
    padding: theme.spacing(1.25, 1.5),
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
  },
  mMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
  },
  statCard: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  statTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.7,
    marginBottom: theme.spacing(1),
  },
  tabs: {
    minHeight: 40,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  tab: {
    minHeight: 40,
    minWidth: 0,
    padding: theme.spacing(0, 2),
    textTransform: 'none',
    fontSize: '0.95rem',
  },
  versus: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: `${theme.spacing(1.5)}px`,
  },
  score: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 10px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.06)',
    fontWeight: 800,
    fontSize: '1rem',
    letterSpacing: '0.04em',
  },
  trophy: {
    fontSize: 16,
    color: '#f9d976',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  statLabel: {
    opacity: 0.7,
  },
  bigValue: {
    fontSize: '1.8em',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
  },
  player: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  classIcon: {
    width: 28,
    height: 28,
    flexShrink: 0,
  },
}));

/** "All" is the default and shows ranked + unranked duels. */
enum DuelFilter {
  All = "All",
  Ranked = "Ranked",
  Unranked = "Unranked",
}

const filterToRanked = (filter: DuelFilter) => filter == DuelFilter.All ? null : filter == DuelFilter.Ranked;

/** Same rule the server uses for win rates: ties don't count against you. */
const getWinRate = (wins: number, loses: number) => wins + loses > 0 ? Math.round((wins / (wins + loses)) * 100) : null;

const StatRow: React.FC<{label: string, value: React.ReactNode}> = ({label, value}) => {
  const classes = useStyles();
  return (
    <div className={classes.statRow}>
      <span className={classes.statLabel}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const RecordCard: React.FC<{title: string, record?: IWinLoss | null, showMmr?: boolean}> = ({title, record, showMmr = true}) => {
  const classes = useStyles();
  let wins = record?.wins || 0;
  let loses = record?.loses || 0;
  let ties = record?.ties || 0;
  let played = record?.played || (wins + loses + ties);
  let winRate = getWinRate(wins, loses);
  return (
    <div className={classes.statCard}>
      <Typography className={classes.statTitle}>{title}</Typography>
      {showMmr ?
        <StatRow label="MMR" value={<span className={classes.bigValue}>{played > 0 && record?.mmr != null ? record.mmr : '-'}</span>} />
      :
        <StatRow label="Win Rate" value={<span className={classes.bigValue}>{winRate != null ? `${winRate}%` : '-'}</span>} />
      }
      {showMmr && <StatRow label="Win Rate" value={winRate != null ? `${winRate}%` : '-'} />}
      <StatRow label="Record" value={
        <span>
          <span style={{color: WIN_COLOR}}>{wins}W</span>
          {' - '}
          <span style={{color: LOSS_COLOR}}>{loses}L</span>
          {ties > 0 && ` - ${ties}T`}
        </span>
      } />
      <StatRow label="Played" value={played} />
    </div>
  );
}

/** One side of the duel; the left one reads right to left so the names meet in the middle. */
const PlayerCell: React.FC<{performance?: IDuelPerformance, winner: boolean, align: 'left' | 'right', highlight?: boolean}> = ({performance, winner, align, highlight}) => {
  const classes = useStyles();
  if(!performance)
    return <span />;
  return (
    <span className={classes.player} style={{justifyContent: align == 'right' ? 'flex-end' : 'flex-start', flexDirection: align == 'right' ? 'row-reverse' : 'row'}}>
      <img className={classes.classIcon} src={classImages.getColorIcon(performance.class as AocClasses)} title={performance.class} />
      <span style={{fontWeight: winner || highlight ? 700 : 400, color: winner ? WIN_COLOR : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis'}}>{performance.name}</span>
      {winner && <EmojiEvents className={classes.trophy} titleAccess="Winner" />}
    </span>
  );
}

interface Props {
  /** show this character's duels (character page) */
  characterId?: string;
  /** show the duels of all of this user's characters (user page) */
  userId?: string;
}

/** The Duels tab on the character and user pages: duel win rates / MMRs, then the duel history. */
export const DuelsTab: React.FC<Props> = ({characterId, userId}) => {
  const history = useHistory();
  const classes = useStyles();
  const page = usePageStyles();
  const mobile = useIsMobile();
  const [filter, setFilter] = useState<DuelFilter>(DuelFilter.All);
  const byUser = !characterId;
  const id = characterId || userId || "";

  const { data: characterRecord } = useQuery<CharacterDuelRecordData, DuelRecordVars>(CharacterDuelRecordQuery, {
    variables: {id},
    skip: byUser || !id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: userRecord } = useQuery<UserDuelRecordData, DuelRecordVars>(UserDuelRecordQuery, {
    variables: {id},
    skip: !byUser || !id,
    fetchPolicy: 'cache-and-network',
  });
  const owner = byUser ? userRecord?.user : characterRecord?.character;

  const feedVariables: DuelFeedVars = {
    cursor: null,
    search: "",
    ranked: filterToRanked(filter),
    character: byUser ? null : id,
    user: byUser ? id : null,
  };
  const { data, loading, fetchMore } = useQuery<DuelFeedData, DuelFeedVars>(DuelFeedQuery, {
    variables: feedVariables,
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const duels = data?.duelFeed?.duels || [];

  const loadMore = () => {
    if(!data?.duelFeed?.hasNext || loading)
      return;
    fetchMore({
      variables: {...feedVariables, cursor: data.duelFeed.next},
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          duelFeed: {
            ...fetchMoreResult.duelFeed,
            duels: _.uniqBy([...prev.duelFeed.duels, ...fetchMoreResult.duelFeed.duels], (duel)=>duel._id?.toString()),
          }
        });
      }
    });
  }

  const isOwn = (performance: IDuelPerformance) => byUser
    ? (performance.user as any)?._id == id
    : (performance.character as any)?._id == id;

  /** this player's side of the duel, from the page owner's point of view */
  const getSides = (duel: IDuel) => {
    let performances = duel.performances || [];
    let own = performances.find(isOwn) || performances[0];
    let opponent = performances.find((p) => p != own);
    return {own, opponent};
  }

  const ranked = owner?.ranked_duel;
  const unranked = owner?.unranked_duel;
  const overall: IWinLoss = {
    wins: (ranked?.wins || 0) + (unranked?.wins || 0),
    loses: (ranked?.loses || 0) + (unranked?.loses || 0),
    ties: (ranked?.ties || 0) + (unranked?.ties || 0),
    played: (ranked?.played || 0) + (unranked?.played || 0),
  } as IWinLoss;

  return (
    <Box>
      <div className={classes.stats}>
        <RecordCard title="All Duels" record={overall} showMmr={false} />
        <RecordCard title="Ranked" record={ranked} />
        <RecordCard title="Unranked" record={unranked} />
      </div>
      <div className={page.tablePanel}>
        {loading && <LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, height: 2}} />}
        <Tabs value={filter} onChange={(event, value) => setFilter(value)} indicatorColor="primary" textColor="primary" className={classes.tabs}>
          <Tab value={DuelFilter.All} label="All" className={classes.tab} />
          <Tab value={DuelFilter.Ranked} label="Ranked" className={classes.tab} />
          <Tab value={DuelFilter.Unranked} label="Unranked" className={classes.tab} />
        </Tabs>
        {mobile ?
          <div>
            {duels.map((duel) => {
              let {own, opponent} = getSides(duel);
              let won = duel.winner != null && (own?.character as any)?._id == duel.winner;
              let tie = duel.winner == null;
              let mmr = byUser ? own?.ummr : own?.mmr;
              let mmrChange = byUser ? own?.ummrChange : own?.mmrChange;
              const when = moment(duel.start_time || duel.createdAt);
              const resultColor = tie ? TIE_COLOR : won ? WIN_COLOR : LOSS_COLOR;
              return (
                <div key={duel._id?.toString()} className={classes.mDuel} onClick={() => history.push(`/duel/${duel._id}`)}
                  style={{boxShadow: `inset 3px 0 0 ${resultColor}`}}>
                  <div className={classes.versus}>
                    <PlayerCell performance={own} winner={!tie && won} align="right" highlight />
                    <span className={classes.score}>
                      <span style={{color: !tie && won ? WIN_COLOR : undefined}}>{own?.roundsWon || 0}</span>
                      <span style={{opacity: 0.4}}>:</span>
                      <span style={{color: !tie && !won ? WIN_COLOR : undefined}}>{opponent?.roundsWon || 0}</span>
                    </span>
                    <PlayerCell performance={opponent} winner={!tie && !won} align="left" />
                  </div>
                  <div className={classes.mMeta}>
                    <Chip size="small" label={tie ? 'Tie' : won ? 'Win' : 'Loss'} style={{background: resultColor, color: '#fff', fontWeight: 600}} />
                    <Chip size="small" color={duel.ranked ? 'primary' : 'default'} label={duel.ranked ? 'Ranked' : 'Unranked'} />
                    {mmr != null &&
                      <span>
                        <span className={page.strong}>{mmr + (mmrChange || 0)}</span>
                        {mmrChange != null && mmrChange != 0 &&
                          <span style={{marginLeft: 4, fontWeight: 600, color: mmrChange > 0 ? WIN_COLOR : LOSS_COLOR}}>{mmrChange > 0 ? '+' : ''}{mmrChange}</span>
                        }
                        <span className={page.muted}> MMR</span>
                      </span>
                    }
                    <span className={page.muted} style={{marginLeft: 'auto'}}>{when.format('MMM D')} · Bo{duel.bestOf || 1} · {formatDuration(duel.timeElapsed)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        :
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={page.headCell}>Date</TableCell>
                <TableCell className={page.headCell}>Type</TableCell>
                <TableCell className={page.headCell} align="center">Duel</TableCell>
                <TableCell className={page.headCell} align="center">Result</TableCell>
                <TableCell className={page.headCell} align="right">MMR</TableCell>
                <TableCell className={page.headCell} align="right">Fight Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {duels.map((duel) => {
                let {own, opponent} = getSides(duel);
                let won = duel.winner != null && (own?.character as any)?._id == duel.winner;
                let tie = duel.winner == null;
                // user pages show the account's mmr, character pages the character's
                let mmr = byUser ? own?.ummr : own?.mmr;
                let mmrChange = byUser ? own?.ummrChange : own?.mmrChange;
                const when = moment(duel.start_time || duel.createdAt);
                const resultColor = tie ? TIE_COLOR : won ? WIN_COLOR : LOSS_COLOR;
                return (
                  <TableRow key={duel._id?.toString()} className={page.row} onClick={() => history.push(`/duel/${duel._id}`)}
                    style={{boxShadow: `inset 3px 0 0 ${resultColor}`}}>
                    <TableCell>
                      <Tooltip title={when.format('MMM Do YYYY, h:mm a')}>
                        <span>
                          <div>{when.format('MMM D, YYYY')}</div>
                          <div className={page.muted}>{when.fromNow()}</div>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={duel.ranked ? 'primary' : 'default'} label={duel.ranked ? 'Ranked' : 'Unranked'} style={{marginRight: 6}} />
                      <Chip size="small" variant="outlined" label={`Best of ${duel.bestOf || 1}`} />
                    </TableCell>
                    <TableCell style={{minWidth: 400}}>
                      <div className={classes.versus}>
                        <PlayerCell performance={own} winner={!tie && won} align="right" highlight />
                        <span className={classes.score}>
                          <span style={{color: !tie && won ? WIN_COLOR : undefined}}>{own?.roundsWon || 0}</span>
                          <span style={{opacity: 0.4}}>:</span>
                          <span style={{color: !tie && !won ? WIN_COLOR : undefined}}>{opponent?.roundsWon || 0}</span>
                        </span>
                        <PlayerCell performance={opponent} winner={!tie && !won} align="left" />
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={tie ? 'Tie' : won ? 'Win' : 'Loss'} style={{background: resultColor, color: '#fff', fontWeight: 600, minWidth: 52}} />
                    </TableCell>
                    <TableCell align="right">
                      {mmr != null ?
                        <span>
                          <span className={page.strong}>{mmr + (mmrChange || 0)}</span>
                          {mmrChange != null &&
                            <span style={{marginLeft: 6, fontWeight: 600, color: mmrChange > 0 ? WIN_COLOR : mmrChange < 0 ? LOSS_COLOR : 'inherit'}}>
                              {mmrChange > 0 ? '+' : ''}{mmrChange}
                            </span>
                          }
                        </span>
                      : <span className={page.muted}>-</span>}
                    </TableCell>
                    <TableCell align="right">{formatDuration(duel.timeElapsed)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        }
        {!loading && duels.length == 0 &&
          <div className={page.empty}>
            <Typography>No {filter == DuelFilter.All ? '' : `${filter.toLowerCase()} `}duels yet.</Typography>
          </div>
        }
        {data?.duelFeed?.hasNext &&
          <React.Fragment>
            {/* scrolling to the end loads the next page; keyed so it fires again if the end is still showing */}
            <Waypoint key={duels.length} onEnter={loadMore} />
            <Button fullWidth className={page.loadMore} disabled={loading} onClick={loadMore}>Load more duels</Button>
          </React.Fragment>
        }
      </div>
    </Box>
  );
};
